<template>
    <div class="phone-input" :class="{ 'phone-input--disabled': disabled }">
        <AccountInput
            :errorMessage="errorMessage"
            :name="name"
            :title="title"
            @inputErrorDetected="emit('inputErrorDetected', name, errorMessage)"
        >
            <div class="phone-input__content">
                <div class="phone-input__prefix">
                    {{ phonePrefix }}
                </div>
                <input
                    class="phone-input__input"
                    type="text"
                    inputmode="numeric"
                    v-model="phoneValue"
                    :id="name"
                    :placeholder="placeholder ?? 'Vaše telefonní číslo'"
                    maxlength="9"
                    required
                    :disabled="disabled"
                    ref="inputRef"
                    @keyup="inputKeyUp"
                />
            </div>
        </AccountInput>
        <div v-if="showTooltip" class="phone-input__tooltip">Na telefon vám v dalším kroku zašleme PIN k ověření.</div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import AccountInput from "./account-input.vue";

const emit = defineEmits<{
    /* eslint-disable @typescript-eslint/naming-convention */
    "update:modelValue": [newValue: string];
    inputKeyUp: [event: KeyboardEvent];
    inputErrorDetected: [fieldName: string, errorMessage: string];
}>();

const props = defineProps<{
    modelValue: string;
    name: string;
    title?: string;
    phoneExtension?: string;
    placeholder?: string;
    showTooltip?: boolean;
    disabled?: boolean;
}>();

const errorMessage = defineModel<string>("errorMessage");

const isPhone = (phone: string) => /^(\d{9})$/.test(phone?.replaceAll(" ", ""));

const modelIsWithPrefix = computed(() => props.modelValue?.length > 3 && props.modelValue[0] == "+");

const phonePrefix = computed(() => {
    return modelIsWithPrefix.value ? props.modelValue.slice(0, 4) : "+420";
});

const phoneValue = computed({
    get() {
        return modelIsWithPrefix.value ? props.modelValue.slice(4) : props.modelValue;
    },
    set(phoneNumber) {
        errorMessage.value = null;
        emit("update:modelValue", phonePrefix.value + phoneNumber);
    },
});

const inputKeyUp = (event: KeyboardEvent) => {
    emit("inputKeyUp", event);
};

const checkIfIsValid = (): boolean => {
    let isValid = true;

    if (!phoneValue.value) {
        errorMessage.value = "Vyplňte vaše telefoní číslo.";
        isValid = false;
    } else if (!isPhone(phoneValue.value)) {
        errorMessage.value = "Chybný formát telefonního čísla.";
        isValid = false;
    }

    return isValid;
};

const inputRef = ref<HTMLInputElement>(null);
onMounted(() => {
    inputRef.value.focus();
});

defineExpose({
    checkIfIsValid,
});
</script>
